/**
 * Sidebar nav icons, ported verbatim (path data unmodified) from app.zip's
 * `src/components/FigmaIcons.tsx` (Figma file GINgWmOz61rBVjpAv72oON, node
 * 49:2175 "Navigation") and `src/components/Icons.tsx` (Admin's hand-authored
 * "sliders" glyph, deliberately not a cog — Admin configures what the
 * objects ARE, a cog reads as app preferences).
 *
 * Wrapped in MUI's `SvgIcon` (rather than app.zip's own bare `<svg>` +
 * `size` prop) so each icon stays a drop-in `SvgIconComponent` — the type
 * `NavItemConfig.icon` already expects, so the call site in
 * `NavSection.tsx` just spreads `SvgIconProps` at it (`<Icon sx={{...}} />`,
 * `fontSize: 16` as of CPA-6) with no per-icon changes needed here.
 * `viewBox` and every path's own `fill`/`stroke`/`strokeWidth`/`transform`
 * are preserved exactly as authored — `currentColor` is what lets each icon
 * follow the nav item through default/hover/active/dark-mode automatically.
 *
 * `NavIconMarketing` is the one exception to "ported from app.zip" above:
 * Marketing doesn't exist in app.zip at all (it was added later, directly in
 * the newer QFly-v2 Figma file, GoPMgYygPGOhoBZAlkcJ1R node 453:3771/453:3789
 * "stock-market 1") — the group previously fell back to a plain MUI icon
 * (`LocalOfferIcon`) for this reason. Verified directly against that node:
 * despite the layer being named "stock-market 1" (a stale name carried over
 * from whatever this instance was duplicated from), the SVG it actually
 * renders is byte-for-byte the same path data as `NavIconClients` above
 * (people/group glyph), just offset +0.125 in both axes from a different
 * export crop — not a chart icon. Kept as its own standalone export (not an
 * alias of `NavIconClients`) so editing one never silently changes the
 * other, even though they render identically today.
 */
import { SvgIcon, type SvgIconProps } from '@mui/material';

export const NavIconDashboard = (props: SvgIconProps): JSX.Element => (
  <SvgIcon {...props} viewBox="0 0 16 16">
    <g fill="none" strokeWidth="1.25" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6.5 0.5H0.5V8.5H6.5V0.5Z" />
      <path d="M6.5 11.5H0.5V15.5H6.5V11.5Z" />
      <path d="M15.5 0.5H9.5V5.5H15.5V0.5Z" />
      <path d="M15.5 8.5H9.5V15.5H15.5V8.5Z" />
    </g>
  </SvgIcon>
);

export const NavIconSales = (props: SvgIconProps): JSX.Element => (
  <SvgIcon {...props} viewBox="0 0 16 16">
    <g
      transform="translate(0.0 1.0)"
      fill="none"
      strokeWidth="1.25"
      stroke="currentColor"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M0.5 13.5H15.5" />
      <path d="M9.5 0.5H6.5V10.5H9.5V0.5Z" />
      <path d="M3.5 5.5H0.5V10.5H3.5V5.5Z" />
      <path d="M15.5 7.5H12.5V10.5H15.5V7.5Z" />
    </g>
  </SvgIcon>
);

export const NavIconClients = (props: SvgIconProps): JSX.Element => (
  <SvgIcon {...props} viewBox="0 0 16 16">
    <g
      transform="translate(0.0 1.0)"
      fill="none"
      strokeWidth="1.25"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5.5 6.5C7.15685 6.5 8.5 5.15685 8.5 3.5C8.5 1.84315 7.15685 0.5 5.5 0.5C3.84315 0.5 2.5 1.84315 2.5 3.5C2.5 5.15685 3.84315 6.5 5.5 6.5Z" />
      <path d="M12.5 5.5C13.6046 5.5 14.5 4.60457 14.5 3.5C14.5 2.39543 13.6046 1.5 12.5 1.5C11.3954 1.5 10.5 2.39543 10.5 3.5C10.5 4.60457 11.3954 5.5 12.5 5.5Z" />
      <path d="M5.5 8.5C4.17392 8.5 2.90215 9.02678 1.96447 9.96447C1.02678 10.9021 0.5 12.1739 0.5 13.5H10.5C10.5 12.1739 9.97322 10.9021 9.03553 9.96447C8.09785 9.02678 6.82608 8.5 5.5 8.5Z" />
      <path d="M10.841 8C11.3324 7.67302 11.9097 7.49903 12.5 7.5C13.2956 7.5 14.0587 7.81607 14.6213 8.37868C15.1839 8.94129 15.5 9.70435 15.5 10.5V11.5H12.5" />
    </g>
  </SvgIcon>
);

export const NavIconContacts = (props: SvgIconProps): JSX.Element => (
  <SvgIcon {...props} viewBox="0 0 16 16">
    <g
      transform="translate(-1.7496 -0.9989) scale(0.899905)"
      fill="none"
      strokeWidth="1.389"
      stroke="currentColor"
      strokeLinecap="round"
    >
      <path d="M12.0731 6.54806C11.9494 6.47413 11.847 6.36939 11.7759 6.24407C11.7048 6.11874 11.6673 5.97711 11.6673 5.833V1.666M11.6673 1.666L5.00066 1.66601C4.55863 1.66601 4.13471 1.84161 3.82215 2.1542C3.50959 2.46678 3.334 2.89074 3.334 3.3328V16.6672C3.334 17.1093 3.50959 17.5332 3.82215 17.8458C4.13471 18.1584 4.55863 18.334 5.00066 18.334H15.0006C15.4426 18.334 15.8665 18.1584 16.1791 17.8458C16.4916 17.5332 16.6672 17.1093 16.6672 16.6672V16.3713M11.6673 1.666C11.9311 1.66558 12.1923 1.71734 12.436 1.81832C12.6797 1.9193 12.9011 2.0675 13.0873 2.25439L15.1839 4.35122M6.66731 15.0004H7.50064M11.9706 11.3619C11.7725 11.5599 11.6274 11.8047 11.5489 12.0736L10.8514 14.4655C10.8305 14.5372 10.8293 14.6132 10.8478 14.6856C10.8663 14.7579 10.904 14.824 10.9568 14.8768C11.0096 14.9296 11.0757 14.9673 11.148 14.9858C11.2204 15.0044 11.2964 15.0031 11.3681 14.9822L13.7597 14.2846C14.0286 14.2061 14.2734 14.0611 14.4714 13.8629L17.8155 10.5218C18.1475 10.1898 18.334 9.73957 18.334 9.27007C18.334 8.80056 18.1475 8.35029 17.8155 8.0183C17.4836 7.68631 17.0334 7.4998 16.5639 7.4998C16.0944 7.4998 15.6442 7.68631 15.3122 8.0183L11.9706 11.3619Z" />
    </g>
  </SvgIcon>
);

export const NavIconLeads = (props: SvgIconProps): JSX.Element => (
  <SvgIcon {...props} viewBox="0 0 16 16">
    <g
      transform="translate(0.0833 0.9167) scale(0.833333)"
      fill="none"
      strokeWidth="1.5"
      stroke="currentColor"
      strokeLinecap="round"
    >
      <path d="M13.1 16.5V14.7222C13.1 13.7792 12.7207 12.8749 12.0456 12.2081C11.3705 11.5413 10.4548 11.1667 9.5 11.1667H4.1C3.14522 11.1667 2.22955 11.5413 1.55442 12.2081C0.879285 12.8749 0.5 13.7792 0.5 14.7222V16.5M15.8 4.94444V10.2778M18.5 7.61111H13.1M10.4 4.05556C10.4 6.01923 8.78822 7.61111 6.8 7.61111C4.81177 7.61111 3.2 6.01923 3.2 4.05556C3.2 2.09188 4.81177 0.5 6.8 0.5C8.78822 0.5 10.4 2.09188 10.4 4.05556Z" />
    </g>
  </SvgIcon>
);

export const NavIconPartners = (props: SvgIconProps): JSX.Element => (
  <SvgIcon {...props} viewBox="0 0 16 16">
    <g
      transform="translate(0.0628 1.0626) scale(0.887992)"
      fill="none"
      strokeWidth="1.4077"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3.3134 3.875C4.24538 3.875 5.0009 3.11948 5.0009 2.1875C5.0009 1.25552 4.24538 0.5 3.3134 0.5C2.38142 0.5 1.6259 1.25552 1.6259 2.1875C1.6259 3.11948 2.38142 3.875 3.3134 3.875Z" />
      <path d="M5.0009 10.625V15.125H0.500901V8.375C0.492354 8.0772 0.544708 7.7808 0.654756 7.50395C0.764804 7.2271 0.930227 6.97565 1.14089 6.76499C1.35155 6.55433 1.60301 6.3889 1.87985 6.27886C2.1567 6.16881 2.45311 6.11645 2.7509 6.125C4.80515 6.125 5.2259 7.99587 7.8134 10.0625" />
      <path d="M12.8759 10.625V15.125H17.3759V8.375C17.3844 8.0772 17.3321 7.7808 17.222 7.50395C17.112 7.2271 16.9466 6.97565 16.7359 6.76499C16.5253 6.55433 16.2738 6.3889 15.9969 6.27886C15.7201 6.16881 15.4237 6.11645 15.1259 6.125C13.0717 6.125 12.6509 7.99587 10.0634 10.0625" />
      <path d="M14.5634 3.875C15.4954 3.875 16.2509 3.11948 16.2509 2.1875C16.2509 1.25552 15.4954 0.5 14.5634 0.5C13.6314 0.5 12.8759 1.25552 12.8759 2.1875C12.8759 3.11948 13.6314 3.875 14.5634 3.875Z" />
    </g>
  </SvgIcon>
);

export const NavIconOpportunities = (props: SvgIconProps): JSX.Element => (
  <SvgIcon {...props} viewBox="0 0 16 16">
    <g
      transform="translate(-0.1356 -0.1356) scale(1.016949)"
      fill="none"
      strokeWidth="1.2292"
      stroke="currentColor"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1.75 8C1.75 4.54822 4.54822 1.75 8 1.75C11.4518 1.75 14.25 4.54822 14.25 8C14.25 11.4518 11.4518 14.25 8 14.25C4.54822 14.25 1.75 11.4518 1.75 8Z" />
      <path d="M13 8H15.375" />
      <path d="M8 0.625V3" />
      <path d="M3 8H0.625" />
      <path d="M8 13V15.375" />
      <path d="M5.5 8C5.5 6.61928 6.61928 5.5 8 5.5C9.38072 5.5 10.5 6.61928 10.5 8C10.5 9.38072 9.38072 10.5 8 10.5C6.61928 10.5 5.5 9.38072 5.5 8Z" />
      <path d="M8.625 8C8.625 8.34519 8.34519 8.625 8 8.625C7.65481 8.625 7.375 8.34519 7.375 8C7.375 7.65481 7.65481 7.375 8 7.375C8.34519 7.375 8.625 7.65481 8.625 8Z" fill="currentColor" />
    </g>
  </SvgIcon>
);

export const NavIconMarketing = (props: SvgIconProps): JSX.Element => (
  <SvgIcon {...props} viewBox="0 0 16.25 14.25">
    <g fill="none" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor">
      <path d="M5.625 6.625C7.28185 6.625 8.625 5.28185 8.625 3.625C8.625 1.96815 7.28185 0.625 5.625 0.625C3.96815 0.625 2.625 1.96815 2.625 3.625C2.625 5.28185 3.96815 6.625 5.625 6.625Z" />
      <path d="M12.625 5.625C13.7296 5.625 14.625 4.72957 14.625 3.625C14.625 2.52043 13.7296 1.625 12.625 1.625C11.5204 1.625 10.625 2.52043 10.625 3.625C10.625 4.72957 11.5204 5.625 12.625 5.625Z" />
      <path d="M5.625 8.625C4.29892 8.625 3.02715 9.15178 2.08947 10.0895C1.15178 11.0271 0.625 12.2989 0.625 13.625H10.625C10.625 12.2989 10.0982 11.0271 9.16053 10.0895C8.22285 9.15178 6.95108 8.625 5.625 8.625Z" />
      <path d="M10.966 8.125C11.4574 7.79802 12.0347 7.62403 12.625 7.625C13.4206 7.625 14.1837 7.94107 14.7463 8.50368C15.3089 9.06629 15.625 9.82935 15.625 10.625V11.625H12.625" />
    </g>
  </SvgIcon>
);

export const NavIconReports = (props: SvgIconProps): JSX.Element => (
  <SvgIcon {...props} viewBox="0 0 16 16">
    <g fill="none" strokeWidth="1.25" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8.5 0.5V7.5L6 5.5L3.5 7.5V0.5" />
      <path d="M0.5 14V2.5C0.5 1.96957 0.710714 1.46086 1.08579 1.08579C1.46086 0.710714 1.96957 0.5 2.5 0.5H13.5C14.0304 0.5 14.5391 0.710714 14.9142 1.08579C15.2893 1.46086 15.5 1.96957 15.5 2.5V14" />
      <path d="M14 12.5H2C1.17157 12.5 0.5 13.1716 0.5 14C0.5 14.8284 1.17157 15.5 2 15.5H14C14.8284 15.5 15.5 14.8284 15.5 14C15.5 13.1716 14.8284 12.5 14 12.5Z" />
    </g>
  </SvgIcon>
);

export const NavIconForecast = (props: SvgIconProps): JSX.Element => (
  <SvgIcon {...props} viewBox="0 0 16 16">
    <g
      transform="translate(0.0 1.0)"
      fill="none"
      strokeWidth="1.25"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2.5 11.5C11 11.5 7 0.5 15.5 0.5" />
      <path d="M15.5 13.5H0.5V0.5" />
    </g>
  </SvgIcon>
);

export const NavIconChat = (props: SvgIconProps): JSX.Element => (
  <SvgIcon {...props} viewBox="0 0 16 16">
    <g
      transform="translate(0.292 0.2144) scale(0.815162)"
      fill="none"
      strokeWidth="1.5334"
      stroke="currentColor"
      strokeLinecap="round"
    >
      <path d="M1.47712 14.4577C1.55432 14.107 1.52486 13.7412 1.39252 13.4074C0.471615 11.4967 0.255153 9.32212 0.781326 7.26735C1.3075 5.21258 2.54249 3.40967 4.26842 2.17672C5.99434 0.943766 8.10027 0.360005 10.2147 0.528432C12.329 0.696858 14.316 1.60665 15.825 3.09728C17.3339 4.58792 18.2679 6.5636 18.4622 8.67576C18.6564 10.7879 18.0984 12.9008 16.8866 14.6417C15.6748 16.3825 13.8871 17.6394 11.8389 18.1906C9.79074 18.7419 7.61366 18.552 5.69182 17.6545C5.37639 17.5347 5.03366 17.5061 4.70272 17.5717L1.63102 18.4699C1.48285 18.5092 1.32708 18.51 1.1785 18.4723C1.02992 18.4345 0.893446 18.3594 0.782022 18.2541C0.670598 18.1488 0.587918 18.0168 0.541821 17.8706C0.495724 17.7244 0.487738 17.5689 0.518622 17.4187L1.47712 14.4577Z" />
    </g>
  </SvgIcon>
);

/** Admin — a "sliders" glyph, hand-authored in app.zip's Icons.tsx (not
 * generated from FigmaIcons.tsx like the rest), deliberately not a cog. */
export const NavIconAdmin = (props: SvgIconProps): JSX.Element => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 6h14M5 12h14M5 18h14" />
      <circle cx="9" cy="6" r="2" fill="currentColor" />
      <circle cx="15" cy="12" r="2" fill="currentColor" />
      <circle cx="10" cy="18" r="2" fill="currentColor" />
    </g>
  </SvgIcon>
);
